import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Upload, Trash2, Plus, Eye, EyeOff, Type,
  RotateCcw, Save, AlignLeft, Layout, Tag, Repeat,
  HelpCircle, DollarSign, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  useLandingConfig, defaultSections, getContrastColor, saveToServer,
} from '../../hooks/useLandingConfig';



function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ═══════════════════════════════════════════════════════
   SECTION META
═══════════════════════════════════════════════════════ */

const SECTION_META = {
  hero:              { label: 'Hero',            icon: Layout,     editable: true  },
  discount_carousel: { label: 'Descuentos',      icon: Tag,        editable: true  },
  marquee:           { label: 'Marquesina',      icon: Repeat,     editable: true  },
  pay_less_intro:    { label: 'Paga menos',      icon: HelpCircle, editable: true  },
  how_it_works:      { label: 'Cómo funciona',   icon: HelpCircle, editable: true  },
  savings_calculator:{ label: 'Calculadora',      icon: Tag,        editable: true  },
  cta_ready:         { label: '¿Estás listo?',   icon: DollarSign, editable: true  },
  pricing:           { label: 'Precios',         icon: DollarSign, editable: true  },
  info_block:        { label: 'Bloque de texto', icon: AlignLeft,  editable: true  },
};

/* ═══════════════════════════════════════════════════════
   SHARED INPUT STYLES
═══════════════════════════════════════════════════════ */

const inputCls = 'w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:border-gray-500 transition-colors';
const labelCls = 'text-gray-600 text-xs uppercase tracking-widest font-semibold block mb-1.5';

/* ═══════════════════════════════════════════════════════
   SORTABLE LOGO ITEM
═══════════════════════════════════════════════════════ */

const SortableLogoItem = ({ logo, onToggle, onDelete, onRename, onToggleForceBlack }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: logo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
        logo.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-gray-500 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
        aria-label="Arrastrar"
      >
        <GripVertical size={18} />
      </button>

      <div className="w-12 h-9 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200">
        {logo.imageUrl ? (
          <img
            src={logo.imageUrl}
            alt={logo.name}
            className="max-w-full max-h-full object-contain"
            /* La miniatura refleja cómo se verá en la marquesina. */
            style={{ filter: logo.forceBlack === false ? 'none' : 'grayscale(100%) brightness(0)' }}
          />
        ) : (
          <span className="text-[10px] font-bold tracking-wider text-gray-600 uppercase">
            {logo.name.slice(0, 4)}
          </span>
        )}
      </div>

      <input
        value={logo.name}
        onChange={e => onRename(logo.id, e.target.value)}
        className="flex-1 bg-transparent text-gray-900 text-sm font-medium outline-none border-b border-transparent focus:border-gray-400 transition-colors"
        placeholder="Nombre de marca"
      />

      {/* Excepción al filtro de negro automático (Módulo 1). Solo aplica a
          logos con imagen: los que se muestran como texto siempre van en negro. */}
      {logo.imageUrl && (
        <button
          onClick={() => onToggleForceBlack(logo.id)}
          className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-colors ${
            logo.forceBlack === false
              ? 'border-gray-300 text-gray-600 hover:text-gray-900'
              : 'border-gray-900 bg-gray-900 text-white hover:bg-gray-700'
          }`}
          title={
            logo.forceBlack === false
              ? 'Conservando color original — clic para forzar a negro'
              : 'Forzado a negro — clic para conservar el color original'
          }
        >
          {logo.forceBlack === false ? 'Color' : 'Negro'}
        </button>
      )}

      <button
        onClick={() => onToggle(logo.id)}
        className={`p-1.5 rounded-lg transition-colors ${
          logo.active ? 'text-gray-700 hover:text-gray-900' : 'text-gray-400 hover:text-gray-600'
        }`}
        title={logo.active ? 'Ocultar del carrusel' : 'Mostrar en carrusel'}
      >
        {logo.active ? <Eye size={16} /> : <EyeOff size={16} />}
      </button>

      <button
        onClick={() => onDelete(logo.id)}
        className="p-1.5 rounded-lg text-red-500 hover:text-red-700 transition-colors"
        title="Eliminar"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   LOGO CAROUSEL MANAGER
═══════════════════════════════════════════════════════ */

const LogoCarouselManager = ({ logos: propLogos, onUpdate }) => {
  const fileRef  = useRef(null);
  const [newName, setNewName] = useState('');
  const sensors  = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const logos = [...propLogos].sort((a, b) => a.order - b.order);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = logos.findIndex(l => l.id === active.id);
    const newIndex = logos.findIndex(l => l.id === over.id);
    onUpdate(arrayMove(logos, oldIndex, newIndex).map((l, i) => ({ ...l, order: i })));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToBase64(file);
      const name    = newName.trim() || file.name.replace(/\.[^.]+$/, '').toUpperCase();
      onUpdate([...logos, { id: uid(), name, imageUrl: dataUrl, active: true, order: logos.length }]);
      setNewName('');
      e.target.value = '';
    } catch { /* ignore */ }
  };

  const addTextLogo = () => {
    const name = newName.trim();
    if (!name) return;
    onUpdate([...logos, { id: uid(), name, imageUrl: '', active: true, order: logos.length }]);
    setNewName('');
  };

  const toggleLogo = (id) => onUpdate(logos.map(l => l.id === id ? { ...l, active: !l.active } : l));
  const deleteLogo = (id) => onUpdate(logos.filter(l => l.id !== id).map((l, i) => ({ ...l, order: i })));
  const renameLogo = (id, name) => onUpdate(logos.map(l => l.id === id ? { ...l, name } : l));
  // Excepción al negro automático. Ausente o true = negro (default del Módulo 1).
  const toggleForceBlack = (id) => onUpdate(
    logos.map(l => l.id === id ? { ...l, forceBlack: l.forceBlack === false } : l)
  );
  const activeCount = logos.filter(l => l.active).length;

  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm">
        Arrastra para reordenar · {activeCount} marca{activeCount !== 1 ? 's' : ''} visible{activeCount !== 1 ? 's' : ''}
      </p>
      <p className="text-gray-500 text-xs">
        Los logos se muestran en negro automáticamente para que la marquesina se vea
        uniforme. Usa el botón <strong>Negro / Color</strong> de cada marca si alguna
        necesita conservar su color original.
      </p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={logos.map(l => l.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {logos.map(logo => (
              <SortableLogoItem
                key={logo.id}
                logo={logo}
                onToggle={toggleLogo}
                onDelete={deleteLogo}
                onRename={renameLogo}
                onToggleForceBlack={toggleForceBlack}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
        <p className="text-gray-700 text-xs font-semibold uppercase tracking-widest">Agregar Marca</p>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addTextLogo()}
          placeholder="Nombre de la marca (ej. ZARA)"
          className={inputCls}
        />
        <div className="flex gap-2">
          <button
            onClick={addTextLogo}
            disabled={!newName.trim()}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-100 border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Type size={15} />
            Texto
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-100 border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            <Upload size={15} />
            Imagen
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/svg+xml,image/png,image/webp,image/jpeg"
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   HERO SECTION EDITOR
═══════════════════════════════════════════════════════ */

const HeroSectionEditor = ({ data, onChange }) => {
  const set = (key, value) => onChange({ ...data, [key]: value });

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white border border-gray-200 p-4">
        <p className="text-gray-500 text-[11px] uppercase tracking-widest font-semibold mb-3">Vista Previa</p>
        <h1 className="font-semibold tracking-tight text-gray-900 text-2xl leading-tight">
          {(data.heroTitle ?? '').split('\\n').map((line, i) => (
            <span key={i}>{i > 0 && <br />}{line}</span>
          ))}
        </h1>
        <p className="text-gray-500 text-sm mt-2">{data.heroSubtitle}</p>
        <button className="mt-3 bg-black text-white rounded-full px-5 py-2 text-xs font-semibold">{data.heroCTA}</button>
        <p className="mt-2 text-xs text-gray-500 underline">{data.footerCTA}</p>
      </div>

      {[
        { key: 'heroTitle',    label: 'Título Principal',   hint: 'Usa \\n para salto de línea', multiline: true, rows: 2 },
        { key: 'heroSubtitle', label: 'Descripción',        multiline: true, rows: 2 },
        { key: 'heroCTA',      label: 'Botón Principal' },
        { key: 'footerCTA',    label: 'Enlace Inferior' },
      ].map(({ key, label, hint, multiline, rows }) => (
        <div key={key}>
          <label className={labelCls}>{label}</label>
          {multiline ? (
            <textarea
              value={data[key] ?? ''}
              onChange={e => set(key, e.target.value)}
              rows={rows}
              className={`${inputCls} resize-none leading-relaxed`}
            />
          ) : (
            <input value={data[key] ?? ''} onChange={e => set(key, e.target.value)} className={inputCls} />
          )}
          {hint && <p className="text-gray-500 text-xs mt-1">{hint}</p>}
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PAGA MENOS (intro) EDITOR
═══════════════════════════════════════════════════════ */

const PayLessIntroEditor = ({ data, onChange }) => {
  const setField = (key, value) => onChange({ ...data, [key]: value });

  return (
    <div className="space-y-4">
      {[
        { key: 'title',    label: 'Título' },
        { key: 'subtitle', label: 'Subtítulo' },
      ].map(({ key, label }) => (
        <div key={key}>
          <label className={labelCls}>{label}</label>
          <input
            value={data[key] ?? ''}
            onChange={e => setField(key, e.target.value)}
            className={inputCls}
          />
        </div>
      ))}

      <div>
        <label className={labelCls}>Texto de la tarjeta (cita)</label>
        <textarea
          value={data.quoteText ?? ''}
          onChange={e => setField('quoteText', e.target.value)}
          rows={3}
          className={`${inputCls} resize-none leading-relaxed`}
        />
      </div>

      <div>
        <label className={labelCls}>Texto del botón</label>
        <input
          value={data.quoteButtonText ?? ''}
          onChange={e => setField('quoteButtonText', e.target.value)}
          className={inputCls}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   HOW IT WORKS EDITOR
═══════════════════════════════════════════════════════ */

const HowItWorksEditor = ({ data, onChange }) => {
  const steps = data.steps ?? [];
  const setStep  = (idx, patch) => onChange({
    ...data,
    steps: steps.map((s, i) => i === idx ? { ...s, ...patch } : s),
  });

  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Qué es vondi</label>
        <textarea
          value={data.descripcion ?? ''}
          onChange={e => onChange({ ...data, descripcion: e.target.value })}
          rows={6}
          placeholder="El párrafo que explica qué es vondi..."
          className={`${inputCls} resize-none leading-relaxed`}
        />
        <p className="text-gray-500 text-xs mt-1">Es el párrafo que va debajo del logo, al cerrar la sección.</p>
      </div>

      <div>
        <label className={labelCls}>Pasos</label>
        <div className="space-y-3">
          {steps.map((step, i) => (
            <div key={i} className="rounded-xl bg-white border border-gray-200 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-black text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {step.n}
                </span>
                <input
                  value={step.t ?? ''}
                  onChange={e => setStep(i, { t: e.target.value })}
                  placeholder="Título del paso"
                  className="flex-1 bg-gray-50 border border-gray-300 rounded-xl px-3 py-2 text-gray-900 text-sm outline-none focus:border-gray-500 transition-colors"
                />
              </div>
              <textarea
                value={step.d ?? ''}
                onChange={e => setStep(i, { d: e.target.value })}
                placeholder="Descripción del paso"
                rows={2}
                className="w-full bg-gray-50 border border-gray-300 rounded-xl px-3 py-2 text-gray-900 text-sm outline-none focus:border-gray-500 transition-colors resize-none leading-relaxed"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PRICING EDITOR
═══════════════════════════════════════════════════════ */

const PricingEditor = ({ data, onChange }) => {
  const features = data.features ?? [];
  const setField   = (key, value) => onChange({ ...data, [key]: value });
  const setFeature = (i, val) => setField('features', features.map((f, j) => j === i ? val : f));
  const addFeature    = () => setField('features', [...features, '']);
  const deleteFeature = (i) => setField('features', features.filter((_, j) => j !== i));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {[
          { key: 'price',  label: 'Precio',  placeholder: '$650' },
          { key: 'period', label: 'Período', placeholder: 'MXN / mes' },
        ].map(({ key, label, placeholder }) => (
          <div key={key}>
            <label className={labelCls}>{label}</label>
            <input
              value={data[key] ?? ''}
              onChange={e => setField(key, e.target.value)}
              placeholder={placeholder}
              className={inputCls}
            />
          </div>
        ))}
      </div>

      {[
        { key: 'ctaText',    label: 'Botón CTA',   placeholder: 'Empezar ahora' },
        { key: 'footerNote', label: 'Nota al pie', placeholder: 'Cobro recurrente vía Stripe · Tarjeta segura' },
      ].map(({ key, label, placeholder }) => (
        <div key={key}>
          <label className={labelCls}>{label}</label>
          <input
            value={data[key] ?? ''}
            onChange={e => setField(key, e.target.value)}
            placeholder={placeholder}
            className={inputCls}
          />
        </div>
      ))}

      <div>
        <label className={labelCls}>Beneficios incluidos</label>
        <div className="space-y-2">
          {features.map((feat, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={feat}
                onChange={e => setFeature(i, e.target.value)}
                placeholder={`Beneficio ${i + 1}`}
                className={inputCls}
              />
              <button
                onClick={() => deleteFeature(i)}
                className="p-2.5 rounded-xl text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors flex-shrink-0"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addFeature}
          className="mt-2 flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
        >
          <Plus size={15} />
          Agregar beneficio
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   INFO BLOCK SECTION EDITOR
═══════════════════════════════════════════════════════ */

const InfoBlockSectionEditor = ({ data, onChange }) => {
  const set = (key, value) => onChange({ ...data, [key]: value });

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 text-base mb-1">{data.title || 'Título del bloque'}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{data.body || 'Texto del bloque...'}</p>
        {data.imageUrl && (
          <img src={data.imageUrl} alt={data.title} className="mt-3 w-full rounded-xl object-cover max-h-40" />
        )}
      </div>

      <div>
        <label className={labelCls}>Título</label>
        <input
          value={data.title ?? ''}
          onChange={e => set('title', e.target.value)}
          placeholder="Título del bloque"
          className={inputCls}
        />
      </div>

      <div>
        <label className={labelCls}>Cuerpo</label>
        <textarea
          value={data.body ?? ''}
          onChange={e => set('body', e.target.value)}
          rows={3}
          placeholder="Escribe el contenido del bloque..."
          className={`${inputCls} resize-none leading-relaxed`}
        />
      </div>

      <div>
        <label className={labelCls}>URL de imagen (opcional)</label>
        <input
          value={data.imageUrl ?? ''}
          onChange={e => set('imageUrl', e.target.value)}
          placeholder="https://... o dejar vacío"
          className={inputCls}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   SECTION INLINE EDITOR (dispatches by type)
═══════════════════════════════════════════════════════ */

const SectionInlineEditor = ({ section, onDataChange }) => {
  const { type, data } = section;

  switch (type) {
    case 'hero':
      return <HeroSectionEditor data={data} onChange={onDataChange} />;

    case 'discount_carousel':
      return (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Título de la sección</label>
            <input
              className={inputCls}
              value={data.label ?? ''}
              placeholder="Lo que vondi te da"
              onChange={e => onDataChange({ ...data, label: e.target.value })}
            />
          </div>
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-center space-y-2">
            <Tag size={22} className="text-gray-400 mx-auto" />
            <p className="text-gray-600 text-sm">Las tarjetas salen de los beneficios activos, una por beneficio.</p>
            <p className="text-gray-500 text-xs">
              Para cambiar cuáles se ven o en qué orden aparecen, arrástralos en la sección "Beneficios".
            </p>
          </div>
        </div>
      );

    /* 'promo_carousel' (Destacados) se retiró: llevaba apagado en producción. */

    case 'marquee':
      return (
        <LogoCarouselManager
          logos={data.logos ?? []}
          onUpdate={(logos) => onDataChange({ ...data, logos })}
        />
      );

    case 'pay_less_intro':
      return <PayLessIntroEditor data={data} onChange={onDataChange} />;

    case 'how_it_works':
      return <HowItWorksEditor data={data} onChange={onDataChange} />;

    case 'savings_calculator':
      return (
        <div className="space-y-4">
          {[
            { key: 'titulo',    label: 'Título',    placeholder: 'Calcula si te conviene comprar vondi' },
            { key: 'subtitulo', label: 'Subtítulo', placeholder: 'Déjalo vacío para no mostrar nada' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className={labelCls}>{label}</label>
              <input
                className={inputCls}
                value={data[key] ?? ''}
                placeholder={placeholder}
                onChange={e => onDataChange({ ...data, [key]: e.target.value })}
              />
            </div>
          ))}

          <div className="pt-3 border-t border-gray-200 space-y-4">
            <p className="text-gray-500 text-xs">
              Justo debajo de la calculadora va el muestrario de precios reales. Sus textos se editan aquí;
              las prendas y sus precios, en la sección "Muestrario".
            </p>
            {[
              { key: 'muestrarioTitulo',    label: 'Título del muestrario',    placeholder: 'Ejemplo de productos y precios reales' },
              { key: 'muestrarioSubtitulo', label: 'Subtítulo del muestrario', placeholder: 'Déjalo vacío para no mostrar nada' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className={labelCls}>{label}</label>
                <input
                  className={inputCls}
                  value={data[key] ?? ''}
                  placeholder={placeholder}
                  onChange={e => onDataChange({ ...data, [key]: e.target.value })}
                />
              </div>
            ))}
          </div>

          <p className="text-gray-500 text-xs">
            Las tiendas y sus porcentajes salen de los beneficios cargados en la base de datos.
          </p>
        </div>
      );

    case 'cta_ready':
      return (
        <div className="space-y-3 p-1">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Título</label>
            <input className={inputCls} value={data.title ?? '¿Estás listo?'}
              onChange={e => onDataChange({ ...data, title: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Texto del botón</label>
            <input className={inputCls} value={data.buttonText ?? 'Acceder'}
              onChange={e => onDataChange({ ...data, buttonText: e.target.value })} />
          </div>
        </div>
      );

    case 'pricing':
      return <PricingEditor data={data} onChange={onDataChange} />;

    case 'info_block':
      return <InfoBlockSectionEditor data={data} onChange={onDataChange} />;

    default:
      return (
        <p className="text-gray-500 text-sm text-center py-4">Tipo desconocido: {type}</p>
      );
  }
};

/* ═══════════════════════════════════════════════════════
   SORTABLE SECTION ROW
═══════════════════════════════════════════════════════ */

const SortableSectionRow = ({ section, expandedId, onToggleExpand, onToggleVisible, onDelete, onDataChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });

  const meta      = SECTION_META[section.type] ?? { label: section.type, icon: AlignLeft, editable: false };
  const Icon      = meta.icon;
  const isVisible = section.visible !== false;
  const isExpanded = expandedId === section.id;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {/* Header row */}
      <div
        className={`flex items-center gap-2 px-3 py-3 bg-white border border-gray-200 transition-colors ${
          isExpanded ? 'rounded-t-2xl border-b-0' : 'rounded-2xl'
        } ${!isVisible ? 'opacity-60' : ''}`}
      >
        <button
          {...attributes}
          {...listeners}
          className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none p-0.5 flex-shrink-0"
          aria-label="Arrastrar"
        >
          <GripVertical size={18} />
        </button>

        <Icon size={15} className="text-gray-500 flex-shrink-0" />

        <span className="flex-1 text-gray-900 text-sm font-medium truncate min-w-0">
          {meta.label}
          {section.type === 'info_block' && section.data?.title && (
            <span className="text-gray-400 font-normal"> — {section.data.title}</span>
          )}
        </span>

        {!isVisible && (
          <span className="text-gray-400 text-xs flex-shrink-0">Oculto</span>
        )}

        <button
          onClick={onToggleVisible}
          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
            isVisible
              ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
          title={isVisible ? 'Ocultar sección' : 'Mostrar sección'}
        >
          {isVisible ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>

        {meta.editable && (
          <button
            onClick={onToggleExpand}
            className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
              isExpanded
                ? 'bg-black text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title={isExpanded ? 'Cerrar editor' : 'Editar sección'}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}

        {section.type === 'info_block' && (
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors flex-shrink-0"
            title="Eliminar bloque"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* Inline editor panel */}
      {isExpanded && meta.editable && (
        <div className="rounded-b-2xl border border-t-0 border-gray-200 bg-gray-50 p-4">
          <SectionInlineEditor section={section} onDataChange={onDataChange} />
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   NAV CTA EDITOR
═══════════════════════════════════════════════════════ */

/* Ya no está «Scroll a sección Precios»: esa sección se retiró del landing y
   ofrecerla aquí sería dejar elegir un destino que no existe. */
const NAV_ROUTES = [
  { value: '/beneficios',    label: 'Ver los beneficios' },
  { value: '/signup',        label: 'Página de registro' },
  { value: '/login',         label: 'Iniciar sesión' },
  { value: '/stores-preview',label: 'Ver tiendas' },
];

const NavCTAEditor = ({ navCTA, onChange }) => {
  const [open, setOpen] = useState(false);
  const cta = navCTA ?? { text: '¡Paga menos!', color: '#0A0A0A', route: '/beneficios' };
  const set = (key, value) => onChange({ ...cta, [key]: value });

  const isPreset = NAV_ROUTES.some(r => r.value === cta.route);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div
          className="w-5 h-5 rounded-full flex-shrink-0 border border-gray-300"
          style={{ background: cta.color }}
        />
        <span className="text-gray-900 font-semibold text-sm flex-1 text-left">
          Botón del nav
        </span>
        <span className="text-gray-400 text-xs mr-1">Aparece al hacer scroll</span>
        <span className="text-gray-500 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100">
          {/* Preview */}
          <div className="mt-4 flex items-center gap-3">
            <p className="text-gray-500 text-[11px] uppercase tracking-widest font-semibold">Vista Previa</p>
            <button
              style={{
                height: 34, borderRadius: 9999, padding: '0 16px',
                background: cta.color, fontSize: 12, fontWeight: 700,
                color: getContrastColor(cta.color), border: 'none', cursor: 'default',
                letterSpacing: '-0.01em',
              }}
            >
              {cta.text || '¡Paga menos!'}
            </button>
          </div>

          {/* Color */}
          <div>
            <label className={labelCls}>Color del botón</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={cta.color}
                onChange={e => set('color', e.target.value)}
                className="w-12 h-10 rounded-xl cursor-pointer border-0 bg-transparent"
                style={{ padding: 2 }}
              />
              <div className="flex-1 rounded-xl overflow-hidden h-10 border border-gray-200">
                <div className="w-full h-full" style={{ background: cta.color }} />
              </div>
              <span className="text-gray-600 text-xs font-mono w-16 text-right">{cta.color}</span>
            </div>
          </div>

          {/* Text */}
          <div>
            <label className={labelCls}>Texto del botón</label>
            <input
              value={cta.text ?? ''}
              onChange={e => set('text', e.target.value)}
              placeholder="¡Paga menos!"
              className={inputCls}
            />
          </div>

          {/* Route */}
          <div>
            <label className={labelCls}>Destino al hacer clic</label>
            <select
              value={isPreset ? cta.route : '__custom__'}
              onChange={e => {
                if (e.target.value !== '__custom__') set('route', e.target.value);
              }}
              className={inputCls}
            >
              {NAV_ROUTES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
              {!isPreset && <option value="__custom__">Ruta personalizada</option>}
            </select>
            {!isPreset && (
              <input
                value={cta.route ?? ''}
                onChange={e => set('route', e.target.value)}
                placeholder="/ruta o #id-elemento"
                className={`${inputCls} mt-2`}
              />
            )}
            <p className="text-gray-500 text-xs mt-1">
              Usa <code className="text-gray-700 font-mono">#id-elemento</code> para scroll o <code className="text-gray-700 font-mono">/ruta</code> para navegar.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN: LANDING EDITOR (page builder)
═══════════════════════════════════════════════════════ */

const LandingEditor = () => {
  const { config, updateConfig, resetConfig } = useLandingConfig();
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveMsg, setSaveMsg]       = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sections = useMemo(
    () => [...(config.sections ?? defaultSections)].sort((a, b) => a.order - b.order),
    [config.sections],
  );

  const patchSection = useCallback((id, patch) => {
    updateConfig({ sections: sections.map(s => s.id === id ? { ...s, ...patch } : s) });
  }, [sections, updateConfig]);

  const patchSectionData = useCallback((id, newData) => {
    updateConfig({ sections: sections.map(s => s.id === id ? { ...s, data: newData } : s) });
  }, [sections, updateConfig]);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.id === active.id);
    const newIdx = sections.findIndex(s => s.id === over.id);
    updateConfig({
      sections: arrayMove(sections, oldIdx, newIdx).map((s, i) => ({ ...s, order: i })),
    });
  };

  const addInfoBlock = () => {
    const newSection = {
      id: uid(),
      type: 'info_block',
      visible: true,
      order: sections.length,
      data: { title: '', body: '', imageUrl: '' },
    };
    updateConfig({ sections: [...sections, newSection] });
    setTimeout(() => setExpandedId(newSection.id), 50);
  };

  const handleSaveToServer = async () => {
    setSaving(true);
    setSaveMsg(null);
    const result = await saveToServer(config);
    setSaving(false);
    setSaveMsg(result.success ? 'ok' : 'error');
    setTimeout(() => setSaveMsg(null), 3000);
  };

  const handleReset = () => {
    if (window.confirm('¿Restablecer la configuración de la landing page a los valores por defecto?')) {
      resetConfig();
      setExpandedId(null);
    }
  };

  const saveLabel = saving           ? 'Guardando...'
    : saveMsg === 'ok'    ? 'Guardado ✓'
    : saveMsg === 'error' ? 'Error al guardar'
    : 'Guardar';

  const saveCls = saving             ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-wait'
    : saveMsg === 'ok'    ? 'bg-green-50 border-green-400 text-green-700'
    : saveMsg === 'error' ? 'bg-red-50 border-red-400 text-red-700'
    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50';

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-2xl bg-[#F5F5F7] min-h-full">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-gray-900 text-xl font-bold">Editor de Landing Page</h2>
          <p className="text-gray-600 text-sm mt-1">
            Arrastra para reordenar · activa o desactiva secciones · edita su contenido.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={handleReset}
            className="p-2 rounded-xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
            title="Restablecer valores por defecto"
          >
            <RotateCcw size={16} />
          </button>
          <button
            onClick={handleSaveToServer}
            disabled={saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${saveCls}`}
          >
            <Save size={15} />
            {saveLabel}
          </button>
        </div>
      </div>

      {/* Nav CTA */}
      <NavCTAEditor
        navCTA={config.navCTA}
        onChange={(navCTA) => updateConfig({ navCTA })}
      />

      {/* Sections list */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sections.map(section => (
              <SortableSectionRow
                key={section.id}
                section={section}
                expandedId={expandedId}
                onToggleExpand={() => setExpandedId(prev => prev === section.id ? null : section.id)}
                onToggleVisible={() => patchSection(section.id, { visible: section.visible === false })}
                onDelete={() => {
                  updateConfig({ sections: sections.filter(s => s.id !== section.id) });
                  if (expandedId === section.id) setExpandedId(null);
                }}
                onDataChange={(newData) => patchSectionData(section.id, newData)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add info block */}
      <button
        onClick={addInfoBlock}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-gray-300 bg-white text-gray-600 hover:text-gray-900 hover:border-gray-400 text-sm font-medium transition-colors"
      >
        <Plus size={16} />
        Agregar bloque de texto
      </button>
    </div>
  );
};

export default LandingEditor;
